import EventString from '@/model/EventClass/EventString'
import EventVar, { VarType } from '@/model/EventClass/EventVar'
import EventVarsEx from '@/model/EventClass/EventVarsEx'
import type { EventFieldOption } from '@/components/eventEditors/eventEditorSchema'

export type VariableBatchTabKey = 'vars' | 'varEx' | 'strings'

export type NumericValueType =
  | VarType.Constant
  | VarType.Other
  | VarType.Random
  | VarType.VarEx
  | VarType.Index
  | VarType.ServerTime
  | VarType.Flower
  | VarType.LocalTime

export type NumericOperationForm = {
  isIndex: boolean
  operator: number
  type: NumericValueType
  constantValue: number
  otherVarIndex: number
  randomMin: number
  randomMax: number
  varExIndex: number
  indexVar: number
  timePart: number
  isResultMinus: boolean
}

export type StringOperationForm = {
  isVarIndex: boolean
  content: string
}

export const VARIABLE_BATCH_GROUP_SIZE = 25

export const VARIABLE_OPERATOR_OPTIONS: EventFieldOption[] = [
  { label: '=', value: 0 },
  { label: '+=', value: 1 },
  { label: '-=', value: 2 },
  { label: '*=', value: 3 },
  { label: '/=', value: 4 },
  { label: '%=', value: 5 },
]

export const VARIABLE_TIME_PART_OPTIONS: EventFieldOption[] = [
  { label: '当前年', value: 0 },
  { label: '当前月', value: 1 },
  { label: '当前日', value: 2 },
  { label: '当前星期', value: 3 },
  { label: '当前小时', value: 4 },
  { label: '当前分钟', value: 5 },
  { label: '当前秒', value: 6 },
  { label: '时间戳', value: 7 },
]

export const VARIABLE_VALUE_TYPE_OPTIONS: EventFieldOption[] = [
  { label: '常量', value: VarType.Constant },
  { label: '其他数值', value: VarType.Other },
  { label: '随机数', value: VarType.Random },
  { label: '二周目变量', value: VarType.VarEx },
  { label: '索引变量', value: VarType.Index },
  { label: '服务器时间', value: VarType.ServerTime },
  { label: '鲜花数', value: VarType.Flower },
  { label: '本地时间', value: VarType.LocalTime },
]

type NumericEvent = EventVar | EventVarsEx

function parseSafeInt(value: string | number | null | undefined, fallback = 0) {
  const next = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10)
  return Number.isFinite(next) ? next : fallback
}

function operatorLabel(operator: number) {
  return VARIABLE_OPERATOR_OPTIONS.find((item) => item.value === operator)?.label ?? '='
}

function timePartLabel(value: number) {
  return VARIABLE_TIME_PART_OPTIONS.find((item) => item.value === value)?.label ?? '时间戳'
}

function variableLabel(index: number, names: string[], kind: '数值' | '二周目' | '字符串', isIndex = false) {
  const name = names[index] ?? ''
  const title = `${kind}${index + 1}`
  const named = name ? `${title}[${name}]` : title
  return isIndex ? `${named}(索引)` : named
}

function parseNumericValue(rawValue: string, prefix: string) {
  return parseSafeInt(rawValue.startsWith(prefix) ? rawValue.slice(prefix.length) : rawValue)
}

function encodeNumericValue(form: NumericOperationForm) {
  switch (form.type) {
    case VarType.Constant:
      return String(form.constantValue)
    case VarType.Other:
      return String(form.otherVarIndex)
    case VarType.Random:
      return `${form.randomMin}|${form.randomMax}`
    case VarType.VarEx:
      return String(form.varExIndex)
    case VarType.Index:
      return String(form.indexVar)
    case VarType.ServerTime:
    case VarType.LocalTime:
      return String(form.timePart)
    case VarType.Flower:
      return ''
    default:
      return ''
  }
}

function decodeNumericValue(type: number, rawValue: string): Pick<
  NumericOperationForm,
  'constantValue' | 'otherVarIndex' | 'randomMin' | 'randomMax' | 'varExIndex' | 'indexVar' | 'timePart'
> {
  const base = {
    constantValue: 0,
    otherVarIndex: 0,
    randomMin: 0,
    randomMax: 100,
    varExIndex: 0,
    indexVar: 0,
    timePart: 0,
  }
  if (type === VarType.Constant) {
    base.constantValue = parseNumericValue(rawValue, 'n|')
    return base
  }
  if (type === VarType.Other) {
    base.otherVarIndex = parseNumericValue(rawValue, 'v|')
    return base
  }
  if (type === VarType.Random) {
    const [minValue, maxValue] = rawValue.split('|')
    base.randomMin = parseSafeInt(minValue)
    base.randomMax = parseSafeInt(maxValue, 100)
    return base
  }
  if (type === VarType.VarEx) {
    base.varExIndex = parseNumericValue(rawValue, 'x|')
    return base
  }
  if (type === VarType.Index) {
    base.indexVar = parseNumericValue(rawValue, 's|')
    return base
  }
  if (type === VarType.ServerTime || type === VarType.LocalTime) {
    base.timePart = parseSafeInt(rawValue)
  }
  return base
}

function describeNumericValue(form: NumericOperationForm, varNames: string[], varExNames: string[]) {
  switch (form.type) {
    case VarType.Constant:
      return String(form.constantValue)
    case VarType.Other:
      return variableLabel(form.otherVarIndex, varNames, '数值')
    case VarType.Random:
      return `随机(${form.randomMin}~${form.randomMax})`
    case VarType.VarEx:
      return variableLabel(form.varExIndex, varExNames, '二周目')
    case VarType.Index:
      return `索引变量${form.indexVar + 1}`
    case VarType.ServerTime:
      return `服务器时间:${timePartLabel(form.timePart)}`
    case VarType.Flower:
      return '鲜花数'
    case VarType.LocalTime:
      return `本地时间:${timePartLabel(form.timePart)}`
    default:
      return ''
  }
}

function applyNumericEventFields(
  event: NumericEvent,
  index: number,
  form: NumericOperationForm,
  names: string[],
  varNames: string[],
  varExNames: string[],
  label: '数值' | '二周目',
) {
  event.Index = index
  event.IsIndex = form.isIndex
  event.Operator = form.operator
  event.Type = form.type
  event.Value = encodeNumericValue(form)
  event.IsResultMinus = form.isResultMinus
  event.Description = `${variableLabel(index, names, label, form.isIndex)} ${operatorLabel(form.operator)} ${describeNumericValue(form, varNames, varExNames)}`
  event.keyUnitInfo = '-1,0'
  event.valueUnitInfo = '-1,0'
}

export function createDefaultNumericOperationForm(): NumericOperationForm {
  return {
    isIndex: false,
    operator: 0,
    type: VarType.Constant,
    constantValue: 0,
    otherVarIndex: 0,
    randomMin: 0,
    randomMax: 100,
    varExIndex: 0,
    indexVar: 0,
    timePart: 0,
    isResultMinus: false,
  }
}

export function createDefaultStringOperationForm(): StringOperationForm {
  return {
    isVarIndex: false,
    content: '',
  }
}

export function cloneNumericOperationForm(form: NumericOperationForm): NumericOperationForm {
  return { ...form }
}

export function cloneStringOperationForm(form: StringOperationForm): StringOperationForm {
  return { ...form }
}

export function readNumericOperationForm(event: Pick<NumericEvent, 'IsIndex' | 'Operator' | 'Type' | 'Value' | 'IsResultMinus'>): NumericOperationForm {
  const type = parseSafeInt(event.Type, VarType.Constant) as NumericValueType
  return {
    isIndex: Boolean(event.IsIndex),
    operator: parseSafeInt(event.Operator),
    type,
    isResultMinus: type === VarType.ServerTime ? Boolean(event.IsResultMinus) : false,
    ...decodeNumericValue(type, String(event.Value ?? '')),
  }
}

export function applyNumericOperationForm(
  event: Pick<NumericEvent, 'IsIndex' | 'Operator' | 'Type' | 'Value' | 'IsResultMinus'>,
  form: NumericOperationForm,
) {
  event.IsIndex = form.isIndex
  event.Operator = form.operator
  event.Type = form.type
  event.Value = encodeNumericValue(form)
  event.IsResultMinus = form.type === VarType.ServerTime ? form.isResultMinus : false
}

export function buildVariableBatchEvents(input: {
  vars: Record<number, NumericOperationForm>
  varEx: Record<number, NumericOperationForm>
  strings: Record<number, StringOperationForm>
  varNames: string[]
  varExNames: string[]
}) {
  const events = []

  for (const index of Object.keys(input.vars).map(Number).sort((a, b) => a - b)) {
    const event = new EventVar()
    applyNumericEventFields(
      event,
      index,
      input.vars[index]!,
      input.varNames,
      input.varNames,
      input.varExNames,
      '数值',
    )
    events.push(event)
  }

  for (const index of Object.keys(input.varEx).map(Number).sort((a, b) => a - b)) {
    const event = new EventVarsEx()
    applyNumericEventFields(
      event,
      index,
      input.varEx[index]!,
      input.varExNames,
      input.varNames,
      input.varExNames,
      '二周目',
    )
    events.push(event)
  }

  for (const index of Object.keys(input.strings).map(Number).sort((a, b) => a - b)) {
    const event = new EventString()
    const config = input.strings[index]!
    event.Index = index
    event.Content = config.content
    event.IsVarIndex = config.isVarIndex
    event.keyUnitInfo = '-1,0'
    events.push(event)
  }

  return events
}

export function describeStringPreview(index: number, names: string[], form: StringOperationForm) {
  const name = variableLabel(index, names, '字符串', form.isVarIndex)
  return `${name} = "${form.content}"`
}
